import axios from "axios";
import { formatCurrency, getCurrencyConfig, API_URL } from "./utils";
import { DOC_TYPE_LABELS, PAYMENT_LABELS, exemptionLabel, processedByText } from "./fiscal";
import { openHtmlDocument } from "./docViewer";

const tpaLabel = (n) => (!n ? "" : n.toUpperCase().startsWith("TPA") ? n : `TPA ${n}`);
const kz = (v) => new Intl.NumberFormat("pt-AO", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0) + " Kz";
const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const SAFE_URL = /^(https?:\/\/|\/|data:image\/(png|jpe?g|gif|webp|svg\+xml);base64,)/i;
const URL_KEYS = new Set(["qr_code_data", "logo_url", "logo_file"]);
// Escapa recursivamente todas as strings (anti-XSS); URLs/imagens só passam se forem http(s), caminho relativo ou data:image
const sanitize = (v, key) => {
  if (Array.isArray(v)) return v.map((x) => sanitize(x));
  if (v && typeof v === "object") return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, sanitize(x, k)]));
  if (typeof v !== "string") return v;
  if (URL_KEYS.has(key)) return SAFE_URL.test(v.trim()) ? v.trim().replace(/"/g, "") : "";
  return esc(v);
};
const unitOf = (item) => item.unit || "un.";
const lineTotalOf = (item) => (item.line_total != null ? item.line_total : item.quantity * item.unit_price);
const isPlainUnit = (u) => !u || /^(un\.?|unid\.?|unidade|unidades|uni)$/i.test(String(u).trim());
// "3x Coca-Cola" / "2 kg Tomate": quantidade sempre à frente, sem "un." quando é unidade simples
const qtyLabel = (qty, unit) => (isPlainUnit(unit) ? `${qty}x` : `${qty} ${unit}`);

// Junta linhas do mesmo produto ao mesmo preço/taxa/desconto numa só (quantidade somada) — a factura mostra «3x Coca-Cola» em vez de três linhas
const lineKey = (it, byNotes) => [it.product_id || "", String(it.product_name || it.description || it.name || "").trim().toLowerCase(), Number(it.unit_price ?? it.price ?? 0).toFixed(2), Number(it.tax_rate ?? -1), it.tax_exemption_reason || "", String(it.unit || "un.").toLowerCase(), Number(it.original_price || 0).toFixed(2), it.promotion_id || it.promotion_name || "", byNotes ? `${it.notes || ""}|${(it.modifiers || []).join(",")}` : ""].join("|");
// opts.byNotes: em vistas operacionais (mesas/cozinha) linhas com notas diferentes ficam separadas
export const mergeLines = (items = [], opts = {}) => {
  const map = new Map();
  for (const raw of items || []) {
    const k = lineKey(raw, opts.byNotes);
    if (!map.has(k)) { map.set(k, { ...raw, quantity: Number(raw.quantity) || 0 }); continue; }
    const m = map.get(k);
    m.quantity += Number(raw.quantity) || 0;
    for (const f of ["line_total", "tax_amount", "discount_amount", "total"]) if (m[f] != null || raw[f] != null) m[f] = Math.round(((Number(m[f]) || 0) + (Number(raw[f]) || 0)) * 100) / 100;
    if ((m.notes || "") !== (raw.notes || "")) m.notes = null;
  }
  return [...map.values()];
};
const discountOf = (item) => Number(item.discount_amount) || 0;
const fmtDateTime = (s) => (s ? String(s).replace("T", " ").slice(0, 19) : "");

// Resumo de IVA por taxa (Anexo I n.º 24 g)) — preços incluem IVA
const taxSummary = (items = []) => {
  const map = {};
  for (const it of items) {
    if (it.tax_rate == null && it.tax_amount == null) continue;
    const rate = Number(it.tax_rate) || 0;
    const gross = Number(lineTotalOf(it)) || 0;
    const tax = it.tax_amount != null ? Number(it.tax_amount) : gross - gross / (1 + rate / 100);
    const k = rate === 0 ? `0|${it.tax_exemption_reason || ""}` : String(rate);
    map[k] = map[k] || { rate, base: 0, tax: 0, reason: rate === 0 ? it.tax_exemption_reason || "" : "" };
    map[k].base += gross - tax;
    map[k].tax += tax;
  }
  return Object.values(map);
};

const buildData = (doc, settings, copyLabel) => {
  const cfg = getCurrencyConfig();
  const logoRaw = settings.logo_url || settings.logo_file || "";
  const logoSrc = logoRaw ? (logoRaw.startsWith("/api") ? `${API_URL.replace("/api", "")}${logoRaw}` : logoRaw) : `${window.location.origin}/lumora-logo.png`;
  const type = doc.doc_type || doc.invoice_type || "FR";
  const number = doc.invoice_number || doc.document_number || "";
  const cancelled = doc.status === "A" || doc.status === "cancelled";
  return {
    cfg,
    isForeign: cfg.currency !== "AOA",
    logoSrc,
    type,
    number,
    cancelled,
    companyName: settings.restaurant_name || settings.business_name || "Lumora",
    typeLabel: DOC_TYPE_LABELS[type] || "Documento",
    paymentLabel:
      type === "CM" || type === "FT"
        ? type === "FT" ? "A crédito / pagamento posterior" : ""
        : (doc.payment_method_label || PAYMENT_LABELS[doc.payment_method] || doc.payment_method || "-") +
          (doc.tpa_name && !(doc.payment_method_label || "").includes(doc.tpa_name) ? ` · ${tpaLabel(doc.tpa_name)}` : ""),
    dateStr: doc.invoice_date || "",
    entryStr: fmtDateTime(doc.system_entry_date),
    copyLabel: copyLabel || "Original",
    processed: processedByText(doc),
    taxes: taxSummary(doc.items),
    singleRate: new Set((doc.items || []).filter((i) => i.tax_rate != null).map((i) => `${Number(i.tax_rate)}|${i.tax_exemption_reason || ""}`)).size <= 1,
    banners: [
      doc.training ? "DOCUMENTO DE FORMAÇÃO — SEM VALOR FISCAL" : "",
      type === "CM" ? "ESTE DOCUMENTO NÃO SERVE DE FACTURA" : "",
      cancelled ? `DOCUMENTO ANULADO${doc.status_date ? " em " + fmtDateTime(doc.status_date) : ""}${doc.cancel_reason || doc.cancellation?.reason ? " — " + (doc.cancel_reason || doc.cancellation?.reason) : ""}` : "",
      doc.source_billing === "M" ? `Cópia do documento original — ${type}M ${doc.manual_series || ""}/${doc.manual_number || ""} de ${doc.manual_date || ""}` : "",
      type === "NC" && doc.related_invoice_number ? `Rectifica a factura ${doc.related_invoice_number}${doc.related_invoice_date ? " de " + doc.related_invoice_date : ""}${doc.reason ? " — Motivo: " + doc.reason : ""}` : "",
      type === "RC" ? "RECIBO — liquidação de factura(s) emitida(s) anteriormente. O IVA foi liquidado na factura de origem." : "",
      (doc.order_references || []).length ? `Ref.: ${doc.order_references.map((r) => r.number).join(", ")}` : "",
    ].filter(Boolean),
  };
};

const taxLines = (d, cls) =>
  d.taxes
    .map(
      (t) =>
        `<div class="${cls}"><span>IVA ${t.rate}%${t.rate === 0 ? ` · ${esc(exemptionLabel(t.reason))}` : ""} (base ${formatCurrency(t.base)})</span><span>${formatCurrency(t.tax)}</span></div>`
    )
    .join("");

// ---------- Térmico 80mm (compacto: ~11cm para 1 artigo) ----------
const thermalTemplate = (doc, settings, d) => {
  const itemLines = (doc.items || [])
    .map((item) => {
      const disc = discountOf(item);
      const qty = Number(item.quantity) || 0;
      const iva = item.tax_rate != null && !d.singleRate ? ` <span class="tax">IVA ${item.tax_rate}%</span>` : "";
      const exempt = Number(item.tax_rate) === 0 && item.tax_exemption_reason && !d.singleRate ? `<div class="sub">${esc(exemptionLabel(item.tax_exemption_reason))}</div>` : "";
      const discLine = disc > 0 ? `<div class="sub flex"><span>Desconto${item.promotion_name ? ` (${item.promotion_name})` : ""}</span><span>-${formatCurrency(disc)}</span></div>` : "";
      // Qtd 1: uma só linha (preço unitário = total). Qtd >1: "3x Nome" + linha "3 x 500,00".
      if (qty === 1 && disc === 0) {
        return `<div class="item flex"><span class="name">${qtyLabel(1, unitOf(item))} ${item.product_name}${iva}</span><span class="amt">${formatCurrency(lineTotalOf(item))}</span></div>${exempt}`;
      }
      return `<div class="item"><div class="flex"><span class="name">${qtyLabel(qty, unitOf(item))} ${item.product_name}${iva}</span><span class="amt">${formatCurrency(lineTotalOf(item))}</span></div><div class="sub">${qty} × ${formatCurrency(item.unit_price)}</div>${exempt}${discLine}</div>`;
    })
    .join("");
  const single = d.taxes.length <= 1;
  const totalsBlock = d.type === "RC" ? "" : `
    ${single ? "" : `<div class="tline"><span>Base tributável</span><span>${formatCurrency(doc.subtotal)}</span></div>`}
    ${doc.discount_total > 0 ? `<div class="tline"><span>Descontos</span><span>-${formatCurrency(doc.discount_total)}</span></div>` : ""}
    ${d.taxes.map((t) => `<div class="tline sub"><span>${single ? "IVA incluído " : "IVA "}${t.rate}%${t.rate === 0 ? ` · ${esc(exemptionLabel(t.reason))}` : ""} s/ ${formatCurrency(t.base)}</span><span>${formatCurrency(t.tax)}</span></div>`).join("")}
    ${single ? "" : `<div class="tline"><span>Total IVA</span><span>${formatCurrency(doc.tax)}</span></div>`}`;
  const receivedBlock = doc.amount_received
    ? `<div class="tline"><span>Recebido</span><span>${formatCurrency(doc.amount_received)}</span></div>${doc.change_given > 0 ? `<div class="tline"><span>Troco</span><span>${formatCurrency(doc.change_given)}</span></div>` : ""}`
    : "";
  const currencyNote = d.isForeign ? `<p class="sub center">Valores em ${d.cfg.currency} · 1 ${d.cfg.currency} = ${kz(d.cfg.rate)} · Total: <b>${kz(doc.total)}</b></p>` : "";
  const bankBlock =
    settings.show_bank_details_on_invoice && settings.bank_iban && d.type !== "CM"
      ? `<p class="sub">${settings.bank_name ? `${settings.bank_name} · ` : ""}IBAN ${settings.bank_iban}</p>`
      : "";
  const qrBlock = doc.qr_code_data && d.type !== "CM" ? `<div class="center"><img class="qr" src="${doc.qr_code_data}" alt="QR" /></div>` : "";
  const banners = d.banners.map((b) => `<p class="banner">${b}</p>`).join("");
  const addr = [settings.address, settings.city].filter(Boolean).join(", ");
  const customer = `Cliente: <b>${doc.customer_name || "Consumidor final"}</b> · NIF ${doc.customer_nif || "999999999"}`;
  const refs = [doc.table_name ? `Mesa ${doc.table_name}` : "", doc.notes || ""].filter(Boolean).join(" · ");

  return `<!DOCTYPE html>
<html lang="pt"><head><meta charset="utf-8" /><title>${d.typeLabel} ${d.number}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  @page { size: 80mm auto; margin: 0; }
  body { width: 72mm; margin: 0 auto; padding: 2mm 1mm 4mm; font-family: Arial, "Helvetica Neue", sans-serif; color: #000; background: #fff; font-size: 11px; line-height: 1.25; font-weight: 700; }
  p { margin: 0; }
  .center { text-align: center; } .sub { font-size: 10px; font-weight: 700; } .flex { display: flex; justify-content: space-between; gap: 4px; }
  .dashed { border-top: 1px dashed #000; margin: 3px 0; }
  .logo { max-width: 30mm; max-height: 12mm; object-fit: contain; display: block; margin: 0 auto 1px; }
  h1 { font-size: 13px; font-weight: 900; line-height: 1.2; }
  .co-info { font-size: 9.5px; font-weight: 700; }
  .doc-title { font-size: 12.5px; font-weight: 900; text-transform: uppercase; margin-top: 3px; }
  .banner { border: 1.5px solid #000; padding: 2px; margin: 3px 0; font-size: 10px; font-weight: 900; text-align: center; }
  .item { margin: 2px 0; } .name { font-weight: 800; } .amt { white-space: nowrap; font-weight: 800; } .tax { font-size: 9px; font-weight: 700; }
  .item .sub { margin-left: 2mm; }
  .tline { display: flex; justify-content: space-between; gap: 4px; font-size: 11px; font-weight: 800; margin: 1px 0; }
  .total { display: flex; justify-content: space-between; font-size: 15px; font-weight: 900; border-top: 2px solid #000; border-bottom: 2px solid #000; padding: 2px 0; margin: 3px 0; }
  .qr { width: 20mm; height: 20mm; margin: 2px auto 0; }
  .footer { font-size: 9px; font-weight: 700; margin-top: 3px; }
  .processed { font-size: 9px; font-weight: 800; word-break: break-all; }
</style></head>
<body>
  <div class="center">
    ${d.logoSrc ? `<img class="logo" src="${d.logoSrc}" alt="logo" />` : ""}
    <h1>${d.companyName}</h1>
    <p class="co-info">NIF ${settings.nif || "-"}${addr ? ` · ${addr}` : ""}${settings.phone ? ` · Tel ${settings.phone}` : ""}</p>
    <p class="doc-title">${d.typeLabel} ${d.number}</p>
    <p class="sub">${d.entryStr || d.dateStr} · ${d.copyLabel}</p>
  </div>
  ${banners}
  <div class="dashed"></div>
  <p class="sub">${customer}</p>
  ${doc.customer_address ? `<p class="sub">${doc.customer_address}</p>` : ""}
  ${refs ? `<p class="sub">${refs}</p>` : ""}
  <div class="dashed"></div>
  ${itemLines}
  <div class="dashed"></div>
  ${totalsBlock}
  <div class="total"><span>TOTAL</span><span>${formatCurrency(doc.total)}</span></div>
  ${d.paymentLabel ? `<div class="tline"><span>Pagamento</span><span>${d.paymentLabel}</span></div>` : ""}
  ${receivedBlock}
  ${currencyNote}
  ${qrBlock}
  ${bankBlock}
  <div class="dashed"></div>
  <div class="center footer">
    ${d.type !== "CM" ? `<p class="processed">${d.processed}</p>` : ""}
    <p>${settings.invoice_footer || "Obrigado pela sua preferência!"} · Documento processado por computador</p>
  </div>
  <script>window.onload = function () { setTimeout(function () { window.print(); }, 350); };</script>
</body></html>`;
};

// ---------- A4 ----------
const a4Template = (doc, settings, d) => {
  const hasDiscount = (doc.items || []).some((i) => discountOf(i) > 0);
  const itemRows = (doc.items || [])
    .map(
      (item) => `
        <tr>
          <td>${item.product_name}${item.promotion_name ? `<div class="sub">Promoção: ${item.promotion_name}</div>` : ""}${Number(item.tax_rate) === 0 && item.tax_exemption_reason ? `<div class="sub">${esc(exemptionLabel(item.tax_exemption_reason))}</div>` : ""}</td>
          <td class="c">${item.quantity}</td>
          <td class="c muted">${unitOf(item)}</td>
          <td class="r mono">${item.original_price && discountOf(item) > 0 ? `<s class="muted">${formatCurrency(item.original_price)}</s> ` : ""}${formatCurrency(item.unit_price)}</td>
          ${hasDiscount ? `<td class="r mono">${discountOf(item) > 0 ? "-" + formatCurrency(discountOf(item)) : "-"}</td>` : ""}
          <td class="c muted">${item.tax_rate != null ? `${item.tax_rate}%` : "-"}</td>
          <td class="r mono">${formatCurrency(lineTotalOf(item))}</td>
        </tr>`
    )
    .join("");
  const receivedBlock = doc.amount_received
    ? `<div class="row"><span>Valor Recebido</span><span class="mono">${formatCurrency(doc.amount_received)}</span></div>
       ${doc.change_given > 0 ? `<div class="row change"><span>Troco</span><span class="mono">${formatCurrency(doc.change_given)}</span></div>` : ""}`
    : "";
  const currencyNote = d.isForeign ? `<p class="note">Valores em ${d.cfg.currency} · 1 ${d.cfg.currency} = ${kz(d.cfg.rate)} · Equivalente do total: <b>${kz(doc.total)}</b></p>` : "";
  const bankBlock =
    settings.show_bank_details_on_invoice && settings.bank_iban && d.type !== "CM"
      ? `<div class="bank"><span class="label">Dados Bancários</span><p>${settings.bank_name || ""}${settings.bank_account_holder ? " · " + settings.bank_account_holder : ""}</p><p class="mono">IBAN: ${settings.bank_iban}</p>${settings.bank_bic ? `<p class="mono">BIC/SWIFT: ${settings.bank_bic}</p>` : ""}</div>`
      : "";
  const qrBlock =
    d.type !== "CM"
      ? `<div class="verify">${doc.qr_code_data ? `<img src="${doc.qr_code_data}" alt="QR" />` : ""}<div><p class="verify-title">Verificação do documento</p><p class="mono">${d.processed}</p><p class="muted">Chave v${doc.key_version || 1} · ${d.copyLabel}</p></div></div>`
      : "";
  const banners = d.banners.map((b) => `<p class="banner">${b}</p>`).join("");

  return `<!DOCTYPE html>
<html lang="pt"><head><meta charset="utf-8" /><title>${d.typeLabel} ${d.number}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { font-family: "Helvetica Neue", Helvetica, Arial, sans-serif; color: #000; background: #fff; max-width: 760px; margin: 0 auto; padding: 24px 28px; font-size: 13px; line-height: 1.35; font-weight: 500; }
  .mono { font-family: "SF Mono", "Courier New", monospace; } .muted { color: #333; } .c { text-align: center; } .r { text-align: right; }
  header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 12px; border-bottom: 2px solid #000; }
  .co { display: flex; gap: 12px; align-items: center; } .co img { max-height: 60px; max-width: 140px; object-fit: contain; }
  .co h1 { font-size: 20px; letter-spacing: -0.02em; font-weight: 800; } .co p { font-size: 12px; font-weight: 600; }
  .doc { text-align: right; } .doc .kind { font-size: 13px; text-transform: uppercase; letter-spacing: 0.1em; font-weight: 800; }
  .doc .num { font-size: 19px; font-weight: 800; } .doc p { font-size: 12px; font-weight: 600; }
  .banner { border: 2px solid #000; padding: 4px 10px; margin-top: 8px; font-weight: 900; text-align: center; letter-spacing: 0.04em; font-size: 12px; }
  .meta { display: flex; justify-content: space-between; gap: 24px; margin: 12px 0 4px; }
  .label { display: block; font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; font-weight: 800; margin-bottom: 4px; border-bottom: 1px solid #000; padding-bottom: 2px; }
  .meta .block p { font-size: 12.5px; font-weight: 600; } .meta .block .strong { font-weight: 800; font-size: 14px; }
  table { width: 100%; border-collapse: collapse; margin-top: 10px; }
  thead th { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; font-weight: 800; text-align: left; padding: 5px 6px; border-bottom: 2px solid #000; border-top: 2px solid #000; }
  thead th.c { text-align: center; } thead th.r { text-align: right; }
  tbody td { padding: 5px 6px; border-bottom: 1px solid #999; font-size: 12.5px; font-weight: 600; } tbody td .sub { font-size: 10.5px; font-weight: 500; color: #333; }
  .totals { display: flex; justify-content: flex-end; margin-top: 8px; } .totals-box { min-width: 320px; }
  .row { display: flex; justify-content: space-between; padding: 2px 6px; font-size: 13px; font-weight: 700; }
  .row.small { font-size: 12.5px; font-weight: 600; }
  .row.total { border-top: 2px solid #000; border-bottom: 2px solid #000; margin-top: 4px; padding: 6px; font-size: 17px; font-weight: 900; }
  .note { margin-top: 8px; font-size: 12px; font-weight: 700; text-align: right; }
  .verify { display: flex; gap: 14px; align-items: center; margin-top: 14px; border: 1.5px solid #000; border-radius: 6px; padding: 8px 12px; }
  .verify img { width: 84px; height: 84px; } .verify p { font-size: 12px; font-weight: 600; word-break: break-all; } .verify .verify-title { font-size: 14px; font-weight: 800; margin-bottom: 2px; }
  .bank { margin-top: 10px; } .bank p { font-size: 12px; font-weight: 600; }
  footer { margin-top: 14px; padding-top: 8px; border-top: 1.5px solid #000; text-align: center; } footer p { font-size: 11px; font-weight: 600; }
  @media print { body { padding: 8px 4px; } .verify, tr { break-inside: avoid; } @page { size: A4; margin: 10mm; } }
</style></head>
<body>
  <header>
    <div class="co">${d.logoSrc ? `<img src="${d.logoSrc}" alt="logo" />` : ""}
      <div><h1>${d.companyName}</h1><p>NIF: ${settings.nif || "-"}</p><p>${settings.address || ""}${settings.city ? ", " + settings.city : ""}</p><p>${settings.phone || ""}${settings.email ? " · " + settings.email : ""}</p></div>
    </div>
    <div class="doc"><p class="kind">${d.typeLabel}</p><p class="num mono">${d.number}</p><p>Data: ${d.dateStr}</p>${d.entryStr ? `<p>Emissão: ${d.entryStr}</p>` : ""}<p>${d.copyLabel}</p></div>
  </header>
  ${banners}
  <div class="meta">
    <div class="block"><span class="label">Cliente</span><p class="strong">${doc.customer_name || "Consumidor final"}</p><p class="muted">NIF: ${doc.customer_nif || "999999999"}</p>${doc.customer_address ? `<p class="muted">${doc.customer_address}</p>` : ""}</div>
    <div class="block" style="text-align:right">${d.paymentLabel ? `<span class="label">Pagamento</span><p class="strong">${d.paymentLabel}</p>` : ""}${doc.table_name ? `<p class="muted">Mesa: ${doc.table_name}</p>` : ""}${doc.notes ? `<p class="muted">${doc.notes}</p>` : ""}</div>
  </div>
  <table><thead><tr><th>Descrição</th><th class="c">Qtd</th><th class="c">Un.</th><th class="r">Preço Unit.</th>${hasDiscount ? '<th class="r">Desconto</th>' : ""}<th class="c">IVA</th><th class="r">Total</th></tr></thead><tbody>${itemRows}</tbody></table>
  <div class="totals"><div class="totals-box">
    ${d.type === "RC" ? "" : `<div class="row"><span class="muted">Base tributável</span><span class="mono">${formatCurrency(doc.subtotal)}</span></div>`}
    ${doc.discount_total > 0 ? `<div class="row"><span class="muted">Descontos</span><span class="mono">-${formatCurrency(doc.discount_total)}</span></div>` : ""}
    ${taxLines(d, "row small")}
    ${d.type === "RC" ? "" : `<div class="row"><span class="muted">Total IVA</span><span class="mono">${formatCurrency(doc.tax)}</span></div>`}
    <div class="row total"><span>Total</span><span class="mono">${formatCurrency(doc.total)}</span></div>
    ${receivedBlock}
  </div></div>
  ${currencyNote}
  ${qrBlock}
  ${bankBlock}
  <footer><p>${settings.invoice_footer || "Obrigado pela sua preferência!"}</p><p>Documento processado por computador</p></footer>
  <script>window.onload = function () { setTimeout(function () { window.print(); }, 350); };</script>
</body></html>`;
};

// Regista a impressão (Original / 2.ª via…) — consultas de mesa (CM) não contam
const registerPrint = async (doc) => {
  if (!doc.invoice_id || doc.doc_type === "CM") return "Original";
  try {
    const r = await axios.post(`${API_URL}/invoices/${doc.invoice_id}/print`, {}, { withCredentials: true });
    return r.data.copy_label || "Original";
  } catch {
    return doc.print_count ? `${doc.print_count + 1}.ª via` : "Original";
  }
};

export const printInvoice = async (rawDoc, rawSettings = {}, format = null) => {
  if (!rawDoc) return;
  const copyLabel = await registerPrint(rawDoc);
  const doc = sanitize({ ...rawDoc, items: mergeLines(rawDoc.items) });
  const settings = sanitize(rawSettings);
  const d = buildData(doc, settings, copyLabel);
  const chosen = format || settings.print_format || "thermal80";
  openHtmlDocument(chosen === "a4" ? a4Template(doc, settings, d) : thermalTemplate(doc, settings, d), { title: `${d.label || "Documento"} ${doc.invoice_number || ""}`.trim(), filename: `${(doc.invoice_number || "documento").replace(/[ /]/g, "_")}.html` });
};

// Recibo (RC): cada factura liquidada é uma linha; sem re-liquidação de IVA
export const receiptToPrintable = (rc) => ({
  ...rc,
  doc_type: "RC",
  invoice_number: rc.document_number,
  items: (rc.settled_invoices || []).map((s) => ({
    product_name: `Factura ${s.invoice_number}${s.invoice_date ? ` de ${s.invoice_date}` : ""}`,
    quantity: 1,
    unit: "doc.",
    unit_price: s.amount,
    line_total: s.amount,
    tax_rate: null,
  })),
  subtotal: rc.total,
  tax: 0,
});

export const printReceipt = async (rc, settings = {}, format = null) => {
  if (!rc) return;
  const doc = sanitize(receiptToPrintable(rc));
  const s = sanitize(settings);
  const d = buildData(doc, s, "Original");
  const chosen = format || s.print_format || "thermal80";
  openHtmlDocument(chosen === "a4" ? a4Template(doc, s, d) : thermalTemplate(doc, s, d), { title: `Recibo ${rc.document_number || ""}`.trim(), filename: `${(rc.document_number || "recibo").replace(/[ /]/g, "_")}.html` });
};
